/**
 * PipelineDetailModal — full per-pair pipeline view.
 *
 * Shows:
 * - Header: creative_id, persona chip, overall status
 * - Phase bands with stage chips and click-to-expand active stage detail
 * - Active stage right rail: metrics, artifacts, substeps
 * - Rollup footer: verdict chip, score, top_strength, critical_gap
 */
import { useState } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import type {
  CreativeReviewPipelineState,
  PipelineStatus,
  StageId,
  PipelineStageState,
} from '@/services/api';
import { PHASE_MANIFEST, STAGE_MAP } from '../pipelineManifest';
import { PipelineStageChip } from './PipelineStageChip';

interface Props {
  pipeline: CreativeReviewPipelineState;
  onClose: () => void;
}

function VerdictBadge({ verdict, score }: { verdict?: string | null; score?: number | null }) {
  if (!verdict) return null;
  const color =
    verdict === 'strong' ? 'text-green-700 bg-green-50 border-green-200' :
    verdict === 'weak' ? 'text-red-700 bg-red-50 border-red-200' :
    'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <span className={clsx('px-2 py-0.5 rounded-full border text-xs font-semibold', color)}>
      {verdict}{score !== null && score !== undefined ? ` · ${score.toFixed(1)}` : ''}
    </span>
  );
}

function MetricGrid({ metrics }: { metrics?: Record<string, string | number | boolean | null> }) {
  if (!metrics || Object.keys(metrics).length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2 mt-2">
      {Object.entries(metrics).map(([k, v]) => (
        <div key={k} className="bg-slate-50 rounded-lg px-3 py-1.5">
          <div className="text-[10px] text-slate-400 uppercase">{k.replace(/_/g, ' ')}</div>
          <div className="text-sm font-medium text-slate-700 truncate">
            {v === null || v === undefined ? '—' : String(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

function StageDetail({ stage }: { stage: PipelineStageState }) {
  return (
    <div className="space-y-2">
      {stage.summary && (
        <p className="text-xs text-slate-600 italic">{stage.summary}</p>
      )}
      <MetricGrid metrics={stage.metrics} />
      {stage.skip_reason && (
        <div className="text-xs text-slate-400 mt-1">
          Skipped: <span className="font-mono">{stage.skip_reason}</span>
        </div>
      )}
      {stage.error && (
        <div className="text-xs text-red-600 mt-1 bg-red-50 px-2 py-1 rounded">
          {stage.error}
        </div>
      )}
      {stage.artifacts?.emitted && stage.artifacts.emitted.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] text-slate-400 uppercase mb-1">Artifacts</div>
          {stage.artifacts.emitted.map((ref, i) => (
            <div key={i} className="text-xs font-mono text-slate-600">
              {ref.store}/{ref.resource}{ref.id ? `: ${ref.id}` : ''}
            </div>
          ))}
        </div>
      )}
      {stage.substeps && stage.substeps.length > 0 && (
        <div className="mt-2 space-y-1">
          <div className="text-[10px] text-slate-400 uppercase">Substeps</div>
          {stage.substeps.map((ss) => (
            <div key={ss.substep_id} className="flex items-center gap-2 text-xs">
              <PipelineStageChip
                label={ss.status}
                status={ss.status as PipelineStatus}
                size="xs"
              />
              <span className="text-slate-600">{ss.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PipelineDetailModal({ pipeline, onClose }: Props) {
  const [expandedStage, setExpandedStage] = useState<StageId | null>(null);

  const overallStatus = pipeline.status as PipelineStatus;
  const r = pipeline.rollup;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <PipelineStageChip label={overallStatus} status={overallStatus} size="xs" />
                <span className="text-xs text-slate-400">pipeline</span>
              </div>
              <h2 className="text-base font-semibold text-slate-900 font-mono truncate max-w-xs">
                {pipeline.creative_id}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5">
                  {pipeline.persona_id}
                </span>
                {pipeline.client_uid && (
                  <span className="text-xs text-slate-400">{pipeline.client_uid}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1 rounded-lg hover:bg-slate-100 text-slate-400 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Phase bands */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {PHASE_MANIFEST.map((phase) => {
            const stagesInPhase = phase.stages.filter(
              (sid) => pipeline.stages?.[sid as StageId],
            );
            if (stagesInPhase.length === 0) return null;

            const phaseStatus = (() => {
              const statuses = stagesInPhase.map(
                (sid) => pipeline.stages?.[sid as StageId]?.status ?? 'pending',
              );
              if (statuses.some(s => s === 'failed')) return 'failed';
              if (statuses.some(s => s === 'running')) return 'running';
              if (statuses.every(s => s === 'completed' || s === 'skipped')) return 'completed';
              return 'pending';
            })() as PipelineStatus;

            return (
              <div key={phase.phase_id}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                    {phase.label}
                  </span>
                  <PipelineStageChip label={phaseStatus} status={phaseStatus} size="xs" />
                </div>
                <div className="space-y-1 pl-3 border-l-2 border-slate-100">
                  {stagesInPhase.map((sid) => {
                    const stageId = sid as StageId;
                    const stageState = pipeline.stages?.[stageId];
                    if (!stageState) return null;
                    const status = stageState.status as PipelineStatus;
                    const manifestEntry = STAGE_MAP[stageId];
                    const isExpanded = expandedStage === stageId;

                    return (
                      <div key={stageId}>
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedStage(isExpanded ? null : stageId)
                          }
                          className={clsx(
                            'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors',
                            isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50',
                          )}
                        >
                          <PipelineStageChip
                            label={manifestEntry?.label ?? stageId}
                            status={status}
                            size="xs"
                          />
                          {stageState.summary && !isExpanded && (
                            <span className="text-xs text-slate-400 truncate flex-1">
                              {stageState.summary}
                            </span>
                          )}
                          <ChevronRight
                            className={clsx(
                              'w-3 h-3 text-slate-300 shrink-0 transition-transform',
                              isExpanded && 'rotate-90',
                            )}
                          />
                        </button>
                        {isExpanded && (
                          <div className="ml-4 mt-1 mb-2 px-3 py-2 bg-slate-50 rounded-lg">
                            <StageDetail stage={stageState} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Rollup footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <VerdictBadge verdict={r?.verdict} score={r?.overall_score} />
            {r?.top_strength && (
              <span className="text-xs text-slate-600">
                <span className="text-green-600 font-medium">↑</span> {r.top_strength}
              </span>
            )}
            {r?.critical_gap && (
              <span className="text-xs text-slate-600">
                <span className="text-red-500 font-medium">↓</span> {r.critical_gap}
              </span>
            )}
            <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
              {(r?.concepts_created ?? 0) > 0 && (
                <span>{r!.concepts_created} concepts created</span>
              )}
              {r?.predictive_impact_computed && (
                <span className="text-teal-600">⚡ impact computed</span>
              )}
              {r?.review_id && (
                <span className="font-mono text-slate-400 truncate max-w-[140px]">
                  {r.review_id}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
